import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './policy.css';

// Reusable animated component
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function PrivacyPolicy() {
  const [activeSection, setActiveSection] = useState('introduction');
  
  // Table of Contents
  const tableOfContents = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'definitions', title: 'Definitions' },
    { id: 'data-collection', title: 'Data Collection' },
    { id: 'data-usage', title: 'Data Usage' },
    { id: 'data-protection', title: 'Data Protection' },
    { id: 'data-retention', title: 'Data Retention' },
    { id: 'user-rights', title: 'Your Rights' },
    { id: 'cookies', title: 'Cookies & Tracking' },
    { id: 'third-parties', title: 'Third Parties' },
    { id: 'international-transfer', title: 'International Transfer' },
    { id: 'children-privacy', title: "Children's Privacy" },
    { id: 'policy-changes', title: 'Policy Changes' },
    { id: 'contact', title: 'Contact Us' }
  ];

  // Last updated date
  const lastUpdated = new Date('2024-01-15').toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Scroll to section
  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Data collection types
  const dataTypes = [
    {
      category: 'Personal Information',
      examples: ['Name', 'Email address', 'Phone number', 'Company name', 'Job title']
    },
    {
      category: 'Technical Data',
      examples: ['IP address', 'Browser type', 'Device information', 'Usage data', 'Cookies']
    },
    {
      category: 'Professional Information',
      examples: ['Business requirements', 'Project details', 'Communication records', 'Service preferences']
    }
  ];

  // Data usage purposes
  const usagePurposes = [
    {
      purpose: 'Service Delivery',
      description: 'To provide and maintain our IT services'
    },
    {
      purpose: 'Communication',
      description: 'To respond to inquiries and provide support'
    },
    {
      purpose: 'Improvement',
      description: 'To enhance and optimize our services'
    },
    {
      purpose: 'Marketing',
      description: 'To send relevant updates and offers (with consent)'
    },
    {
      purpose: 'Security',
      description: 'To protect our services and users'
    },
    {
      purpose: 'Compliance',
      description: 'To meet legal and regulatory obligations'
    }
  ];

  return (
    <div className="policy-container">
      {/* ===== HEADER SECTION ===== */}
      <section className="policy-hero">
        <div className="hero-background">
          <div className="hero-pattern"></div>
        </div>
        <motion.div 
          className="policy-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-badge">
            <span>Data Protection</span>
          </div>
          <h1>Privacy <span className="gradient-text">Policy</span></h1>
          <p className="hero-subtitle">
            At LEARNEX, we are committed to protecting your privacy and ensuring the security of your 
            personal information. This policy explains how we collect, use, and safeguard your data.
          </p>
          <div className="last-updated">
            Last updated: {lastUpdated}
          </div>
        </motion.div>
      </section>

      {/* ===== QUICK SUMMARY ===== */}
      <AnimatedSection className="policy-summary">
        <div className="summary-cards">
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">🔒</div>
            <h3>Security First</h3>
            <p>Enterprise-grade protection for your data</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">📋</div>
            <h3>Transparency</h3>
            <p>Clear explanation of our practices</p>
          </motion.div>
          
          <motion.div 
            className="summary-card"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.2 }}
          >
            <div className="summary-icon">⚖️</div>
            <h3>Compliance</h3>
            <p>GDPR, CCPA, and PDPA compliant</p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* ===== MAIN CONTENT ===== */}
      <div className="policy-content-container">
        {/* ===== TABLE OF CONTENTS ===== */}
        <AnimatedSection className="table-of-contents">
          <div className="toc-header">
            <h2>Table of Contents</h2>
            <p>Navigate through our privacy policy</p>
          </div>
          <nav className="toc-nav">
            <ul>
              {tableOfContents.map((item) => (
                <li key={item.id}>
                  <button
                    className={`toc-link ${activeSection === item.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(item.id)}
                  >
                    {item.title}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </AnimatedSection>

        {/* ===== POLICY CONTENT ===== */}
        <div className="policy-sections">
          {/* INTRODUCTION */}
          <AnimatedSection id="introduction" className="policy-section">
            <h2>1. Introduction</h2>
            <p>
              LEARNEX Technologies ("we", "our", "us") is committed to protecting and respecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
              you visit our website learnex.lk or use our services.
            </p>
            <p>
              We comply with the Sri Lankan Personal Data Protection Act No. 9 of 2022, the EU General Data 
              Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable 
              data protection laws.
            </p>
            <div className="compliance-badges">
              <span className="compliance-badge">GDPR</span>
              <span className="compliance-badge">CCPA</span>
              <span className="compliance-badge">PDPA</span>
              <span className="compliance-badge">ISO 27001</span>
            </div>
          </AnimatedSection>

          {/* DEFINITIONS */}
          <AnimatedSection id="definitions" className="policy-section">
            <h2>2. Definitions</h2>
            <div className="definitions-grid">
              <div className="definition-item">
                <h4>Personal Data</h4>
                <p>Any information relating to an identified or identifiable natural person</p>
              </div>
              <div className="definition-item">
                <h4>Processing</h4>
                <p>Any operation performed on personal data (collection, storage, use, etc.)</p>
              </div>
              <div className="definition-item">
                <h4>Data Controller</h4>
                <p>LEARNEX determines the purposes and means of processing personal data</p>
              </div>
              <div className="definition-item">
                <h4>Data Processor</h4>
                <p>Third parties that process data on behalf of LEARNEX</p>
              </div>
            </div>
          </AnimatedSection>

          {/* DATA COLLECTION */}
          <AnimatedSection id="data-collection" className="policy-section">
            <h2>3. Data We Collect</h2>
            <p>
              We collect information that you provide directly to us, as well as information automatically 
              collected through your use of our services:
            </p>
            
            <div className="data-types">
              {dataTypes.map((type, index) => (
                <motion.div 
                  key={index}
                  className="data-type-card"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <h4>{type.category}</h4>
                  <ul>
                    {type.examples.map((example, i) => (
                      <li key={i}>{example}</li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
            
            <p>
              We collect this information through various methods including website forms, cookies, 
              communication channels, and service usage.
            </p>
          </AnimatedSection>

          {/* DATA USAGE */}
          <AnimatedSection id="data-usage" className="policy-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use your personal data for the following purposes:</p>
            
            <div className="usage-purposes">
              {usagePurposes.map((item, index) => (
                <motion.div 
                  key={index}
                  className="purpose-item"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="purpose-number">{index + 1}</div>
                  <div className="purpose-content">
                    <h4>{item.purpose}</h4>
                    <p>{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <p className="legal-basis">
              <strong>Legal Basis for Processing:</strong> We process your data based on your consent, 
              contract performance, legal obligations, or legitimate interests as defined by applicable 
              data protection laws.
            </p>
          </AnimatedSection>

          {/* DATA PROTECTION */}
          <AnimatedSection id="data-protection" className="policy-section">
            <h2>5. Data Protection & Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal 
              data against unauthorized access, alteration, disclosure, or destruction.
            </p>
            
            <div className="security-measures">
              <div className="security-card">
                <div className="security-icon">🔐</div>
                <h4>Encryption</h4>
                <p>All data is encrypted in transit and at rest using industry-standard protocols</p>
              </div>
              <div className="security-card">
                <div className="security-icon">🛡️</div>
                <h4>Access Controls</h4>
                <p>Strict role-based access controls and authentication mechanisms</p>
              </div>
              <div className="security-card">
                <div className="security-icon">📊</div>
                <h4>Monitoring</h4>
                <p>Continuous security monitoring and intrusion detection systems</p>
              </div>
              <div className="security-card">
                <div className="security-icon">🔄</div>
                <h4>Backups</h4>
                <p>Regular backups and disaster recovery procedures</p>
              </div>
            </div>
            
            <p>
              Our security practices are regularly reviewed and updated to address emerging threats 
              and vulnerabilities.
            </p>
          </AnimatedSection>

          {/* DATA RETENTION */}
          <AnimatedSection id="data-retention" className="policy-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain personal data only for as long as necessary to fulfill the purposes for which 
              it was collected, including to satisfy any legal, accounting, or reporting requirements.
            </p>
            
            <div className="retention-table">
              <div className="table-header">
                <span>Data Type</span>
                <span>Retention Period</span>
                <span>Basis</span>
              </div>
              <div className="table-row">
                <span>Client project data</span>
                <span>7 years after project completion</span>
                <span>Legal requirements</span>
              </div>
              <div className="table-row">
                <span>Marketing data</span>
                <span>3 years after last contact</span>
                <span>Legitimate interest</span>
              </div>
              <div className="table-row">
                <span>Website analytics</span>
                <span>26 months</span>
                <span>Service improvement</span>
              </div>
              <div className="table-row">
                <span>Job applicant data</span>
                <span>2 years after application</span>
                <span>Recruitment purposes</span>
              </div>
            </div>
          </AnimatedSection>

          {/* USER RIGHTS */}
          <AnimatedSection id="user-rights" className="policy-section">
            <h2>7. Your Data Protection Rights</h2>
            <p>
              Depending on your location and applicable laws, you may have the following rights 
              regarding your personal data:
            </p>
            
            <div className="rights-grid">
              <div className="right-item">
                <h4>Right to Access</h4>
                <p>Request copies of your personal data</p>
              </div>
              <div className="right-item">
                <h4>Right to Rectification</h4>
                <p>Request correction of inaccurate data</p>
              </div>
              <div className="right-item">
                <h4>Right to Erasure</h4>
                <p>Request deletion of your personal data</p>
              </div>
              <div className="right-item">
                <h4>Right to Restriction</h4>
                <p>Request limiting processing of your data</p>
              </div>
              <div className="right-item">
                <h4>Right to Data Portability</h4>
                <p>Request transfer of data to another organization</p>
              </div>
              <div className="right-item">
                <h4>Right to Object</h4>
                <p>Object to processing of your personal data</p>
              </div>
            </div>
            
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:privacy@learnex.lk">privacy@learnex.lk</a>. We will respond to your 
              request within 30 days as required by law.
            </p>
          </AnimatedSection>

          {/* COOKIES */}
          <AnimatedSection id="cookies" className="policy-section">
            <h2>8. Cookies & Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our service and 
              hold certain information. Cookies are files with a small amount of data which may include 
              an anonymous unique identifier.
            </p>
            
            <div className="cookie-types">
              <div className="cookie-type">
                <h4>Essential Cookies</h4>
                <p>Required for the website to function properly</p>
              </div>
              <div className="cookie-type">
                <h4>Analytics Cookies</h4>
                <p>Help us understand how visitors interact with our website</p>
              </div>
              <div className="cookie-type">
                <h4>Marketing Cookies</h4>
                <p>Used to track visitors across websites for marketing purposes</p>
              </div>
            </div>
            
            <p>
              You can control cookies through your browser settings. For more detailed information, 
              please see our <a href="/cookie-policy">Cookie Policy</a>.
            </p>
          </AnimatedSection>

          {/* THIRD PARTIES */}
          <AnimatedSection id="third-parties" className="policy-section">
            <h2>9. Third-Party Disclosures</h2>
            <p>
              We may share your information with third parties in the following situations:
            </p>
            
            <div className="third-party-list">
              <div className="third-party-item">
                <h4>Service Providers</h4>
                <p>Cloud hosting, analytics, customer support, and marketing services</p>
              </div>
              <div className="third-party-item">
                <h4>Business Partners</h4>
                <p>Technology partners for integrated service delivery</p>
              </div>
              <div className="third-party-item">
                <h4>Legal Requirements</h4>
                <p>When required by law, regulation, or legal process</p>
              </div>
              <div className="third-party-item">
                <h4>Business Transfers</h4>
                <p>In connection with a merger, acquisition, or sale of assets</p>
              </div>
            </div>
            
            <p>
              All third-party service providers are carefully vetted and required to maintain 
              appropriate security measures and data protection standards.
            </p>
          </AnimatedSection>

          {/* INTERNATIONAL TRANSFER */}
          <AnimatedSection id="international-transfer" className="policy-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than Sri Lanka, 
              where data protection laws may differ. We ensure appropriate safeguards are in place for 
              such transfers, including:
            </p>
            
            <ul>
              <li>EU Standard Contractual Clauses</li>
              <li>Adequacy decisions where applicable</li>
              <li>Binding corporate rules for intra-company transfers</li>
              <li>Certification mechanisms such as Privacy Shield (where valid)</li>
            </ul>
            
            <p>
              You can obtain more information about the safeguards we use for international transfers 
              by contacting our Data Protection Officer.
            </p>
          </AnimatedSection>

          {/* CHILDREN'S PRIVACY */}
          <AnimatedSection id="children-privacy" className="policy-section">
            <h2>11. Children's Privacy</h2>
            <p>
              Our services are not intended for children under the age of 16. We do not knowingly 
              collect personal information from children under 16. If you become aware that a child 
              has provided us with personal information, please contact us immediately.
            </p>
            
            <p>
              If we become aware that we have collected personal information from children without 
              verification of parental consent, we take steps to remove that information from our servers.
            </p>
          </AnimatedSection>

          {/* POLICY CHANGES */}
          <AnimatedSection id="policy-changes" className="policy-section">
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
            
            <p>
              We will also provide notice via email and/or a prominent notice on our service prior to 
              the change becoming effective, when required by law.
            </p>
            
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to 
              this Privacy Policy are effective when they are posted on this page.
            </p>
          </AnimatedSection>

          {/* CONTACT */}
          <AnimatedSection id="contact" className="policy-section">
            <h2>13. Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please 
              contact our Data Protection Officer:
            </p>
            
            <div className="contact-details">
              <div className="contact-item">
                <strong>LEARNEX Technologies</strong>
                <p>33, Sri Dharmarama Road</p>
                <p>Ratmalana, Sri Lanka</p>
              </div>
              <div className="contact-item">
                <strong>Data Protection Officer</strong>
                <p>Email: <a href="mailto:dpo@learnex.lk">dpo@learnex.lk</a></p>
                <p>Phone: +94 70 1111 735</p>
              </div>
              <div className="contact-item">
                <strong>General Inquiries</strong>
                <p>Email: <a href="mailto:privacy@learnex.lk">privacy@learnex.lk</a></p>
                <p>Phone: +94 76 5610 017</p>
              </div>
            </div>
            
            <p className="supervisory-authority">
              <strong>Supervisory Authority:</strong> You have the right to lodge a complaint with 
              the relevant data protection authority in your jurisdiction.
            </p>
          </AnimatedSection>
        </div>
      </div>

      {/* ===== CONSENT SECTION ===== */}
      <AnimatedSection className="consent-section">
        <div className="consent-content">
          <h2>Your Privacy Matters</h2>
          <p>
            By using our services, you acknowledge that you have read and understood this Privacy Policy. 
            We are committed to protecting your privacy and handling your data with care and respect.
          </p>
          {/* <div className="consent-actions">
            <motion.button 
              className="btn btn-primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Download Policy (PDF)
            </motion.button>
            <motion.button 
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Manage Preferences
            </motion.button>
          </div> */}
        </div>
      </AnimatedSection>
    </div>
  );
}

export default PrivacyPolicy;